import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';

/**
 * ProtectedSuperAdminRoute - Only accessible to super_admin role
 */
const ProtectedSuperAdminRoute = ({ children }) => {
  const location = useLocation();
  const [status, setStatus] = useState(null); // null=checking, 'ok', 'denied', 'no_auth'

  useEffect(() => {
    let cancelled = false;

    const checkAuth = async () => {
      try {
        const { data: { session }, error } = await supabase?.auth?.getSession();
        if (error || !session) {
          if (!cancelled) setStatus('no_auth');
          return;
        }

        // Check user_profiles for super_admin role
        const { data: profile } = await supabase
          ?.from('user_profiles')
          ?.select('role, is_active')
          ?.eq('id', session?.user?.id)
          ?.single();

        if (!profile || profile?.role !== 'super_admin' || !profile?.is_active) {
          if (!cancelled) setStatus('denied');
          return;
        }

        if (!cancelled) setStatus('ok');
      } catch (err) {
        if (!cancelled) setStatus('no_auth');
      }
    };

    checkAuth();

    const { data: authListener } = supabase?.auth?.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' || !session) {
        if (!cancelled) setStatus('no_auth');
      }
    });

    return () => {
      cancelled = true;
      authListener?.subscription?.unsubscribe();
    };
  }, []);

  if (status === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Vérification des permissions...</p>
        </div>
      </div>
    );
  }

  if (status === 'no_auth') {
    return <Navigate to="/admin-login" replace state={{ from: location }} />;
  }

  if (status === 'denied') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="h-10 w-10 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">Accès refusé</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Vous n'avez pas l'autorisation d'accéder à cette page.
          </p>
          <button
            onClick={() => window.history?.back()}
            className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
          >
            Retour
          </button>
        </div>
      </div>
    );
  }

  return children;
};

export default ProtectedSuperAdminRoute;
